import React, { useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ActivityIndicator, Modal as RNModal, ScrollView } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, spacing, typography } from '../theme';
import { designTokens } from '../theme/design-system';
import { useLocale } from '../contexts/LocaleContext';
import { useRTLStyles } from '../hooks/useRTLStyles';
import { PermissionSummary } from '../constants/permissionLevels';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { withAlpha } from '../theme';

interface PermissionSummaryChipProps {
  targetType: 'user' | 'department';
  targetId: string;
  summary: PermissionSummary | null | undefined;
  loading?: boolean;
  onPress?: () => void;
}

const CATEGORY_LABELS: Record<string, string> = {
  general: 'General',
  wabu: 'WhatsApp',
  fbm: 'Messenger',
  sms: 'SMS',
  email: 'Email',
  telegram: 'Telegram',
  instagram: 'Instagram',
  contacts: 'Contacts',
  reports: 'Reports',
  notifications: 'Notifications',
  storage: 'Storage',
};

export const PermissionSummaryChip: React.FC<PermissionSummaryChipProps> = ({ summary, loading, onPress }) => {
  const styles = useThemedStyles(getStyles);
  const { t } = useLocale();
  const rtl = useRTLStyles();
  const [expanded, setExpanded] = useState(false);

  if (loading) {
    return (
      <View style={[styles.chip, { flexDirection: rtl.rowDirection }]}>
        <ActivityIndicator size="small" color={colors.primary} />
      </View>
    );
  }

  const categories = summary?.byCategory ? Object.entries(summary.byCategory) : [];
  const totalAtViewOrAbove = categories.reduce((sum, [, c]) => sum + c.atViewOrAbove, 0);

  return (
    <View>
      <TouchableOpacity
        style={[styles.chip, { flexDirection: rtl.rowDirection }]}
        onPress={() => setExpanded(prev => !prev)}
        activeOpacity={0.7}
      >
        <Ionicons name="shield-checkmark-outline" size={12} color={colors.primary} />
        <Text style={styles.chipText}>
          {t('permissions.summaryChip.label', { count: totalAtViewOrAbove })}
        </Text>
        <Ionicons name={expanded ? 'chevron-up' : 'chevron-down'} size={12} color={colors.text.secondary} />
      </TouchableOpacity>

      <RNModal
        visible={expanded}
        transparent
        animationType="fade"
        onRequestClose={() => setExpanded(false)}
      >
        <TouchableOpacity style={styles.backdrop} activeOpacity={1} onPress={() => setExpanded(false)}>
          <TouchableOpacity style={styles.popupCard} activeOpacity={1}>
            <View style={[styles.popupHeader, { flexDirection: rtl.rowDirection }]}>
              <View style={[styles.popupTitleRow, { flexDirection: rtl.rowDirection }]}>
                <Ionicons name="shield-checkmark-outline" size={18} color={colors.primary} />
                <Text style={styles.popupTitle}>{t('permissions.summaryChip.label', { count: totalAtViewOrAbove })}</Text>
              </View>
              <TouchableOpacity onPress={() => setExpanded(false)} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
                <Ionicons name="close" size={20} color={colors.text.secondary} />
              </TouchableOpacity>
            </View>
            <ScrollView style={styles.popupScroll} contentContainerStyle={[styles.expandedContainer, { flexDirection: rtl.rowDirection, flexWrap: 'wrap' }]}>
              {categories.length === 0 ? (
                <Text style={styles.emptyText}>{t('permissions.summaryChip.empty')}</Text>
              ) : (
                categories.map(([category, counts]) => (
                  <View key={category} style={styles.miniChip}>
                    <Text style={styles.miniChipText}>
                      {CATEGORY_LABELS[category] || category}: {counts.atViewOrAbove}
                    </Text>
                  </View>
                ))
              )}
            </ScrollView>
            {onPress && (
              <TouchableOpacity
                style={styles.manageButton}
                onPress={() => {
                  setExpanded(false);
                  onPress();
                }}
                activeOpacity={0.7}
              >
                <Text style={styles.manageButtonText}>{t('permissions.summaryChip.manageLink')}</Text>
              </TouchableOpacity>
            )}
          </TouchableOpacity>
        </TouchableOpacity>
      </RNModal>
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  chip: {
    alignItems: 'center',
    gap: spacing.xs,
    backgroundColor: withAlpha(colors.primary, 0.06),
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs / 1.5,
    borderRadius: designTokens.borderRadius?.full || 999,
    alignSelf: 'flex-start',
  },
  chipText: {
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.medium,
    color: colors.primary,
  },
  expandedContainer: {
    gap: spacing.xs,
    alignItems: 'center',
    padding: spacing.md,
  },
  miniChip: {
    backgroundColor: colors.background.secondary,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs / 2,
    borderRadius: 8,
  },
  miniChipText: {
    fontSize: typography.fontSize.xs,
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.regular,
  },
  emptyText: {
    fontSize: typography.fontSize.xs,
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.regular,
  },
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(15, 23, 42, 0.42)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: spacing.lg,
  },
  popupCard: {
    width: '100%',
    maxWidth: 420,
    maxHeight: '80%',
    backgroundColor: colors.background.surface,
    borderRadius: designTokens.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
    overflow: 'hidden',
  },
  popupHeader: {
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  popupTitleRow: {
    alignItems: 'center',
    gap: spacing.sm,
    flex: 1,
  },
  popupTitle: {
    fontSize: typography.fontSize.base,
    color: colors.text.primary,
    fontFamily: typography.fontFamily.semibold,
  },
  popupScroll: {
    maxHeight: 280,
  },
  manageButton: {
    margin: spacing.md,
    marginTop: 0,
    backgroundColor: colors.primary,
    borderRadius: designTokens.borderRadius.sm,
    paddingVertical: spacing.sm,
    alignItems: 'center',
  },
  manageButtonText: {
    fontSize: typography.fontSize.sm,
    color: colors.white,
    fontFamily: typography.fontFamily.semibold,
  },
});
